import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "@Constants/queryKeys";
import { UsersApiService } from "@Api/users-service";
import NotificationService from "../../../services/NotificationService";
import { ResponseType } from "@Utils/types";

export const useUserProfile = () => {
  return useQuery({
    queryKey: [queryKeys.userManagement.profile],
    queryFn: async () => {
      const { ok, response, data } = await UsersApiService.getProfileDetails();
      if (ok) {
        return data?.data;
      }
      throw response?.message;
    },
  });
};

export const useUserTotalStats = () => {
  return useQuery({
    queryKey: [queryKeys.userManagement.stats],
    queryFn: async () => {
      const { ok, response, data } = await UsersApiService.getUsersStats();
      if (ok) {
        return data;
      }
      throw response?.message;
    },
  });
};

export const useUserDetails = (id: string, type: string) => {
  return useQuery({
    queryKey: [queryKeys.userManagement.details, id],
    queryFn: async () => {
      const { ok, response, data } = await UsersApiService.getUserDetails({
        id,
        type,
      });
      if (ok) {
        return data?.data;
      }
      throw response?.message;
    },
  });
};

export const useDeleteUser = () => {
  const queryClient = useQueryClient();
  return useMutation<ResponseType, Error, any>({
    mutationFn: (id: any) => UsersApiService.deleteUser(id),
    onSuccess: ({ ok, response, data }: ResponseType) => {
      if (ok) {
        NotificationService.success("User deleted successfully.");
      }
    },
  });
};

export const updateUserStatus = () => {
  const queryClient = useQueryClient();
  return useMutation<ResponseType, Error, any>({
    mutationFn: (payload: any) =>
      UsersApiService.updateUserStatus(payload.userId, payload.accountStatus),
    onSuccess: ({ ok, response, data }: ResponseType) => {
      if (ok) {
        NotificationService.success("User Status updated successfully.");
        queryClient.invalidateQueries({
          queryKey: [queryKeys.userManagement.details],
        });
        queryClient.invalidateQueries({
          queryKey: [queryKeys.userManagement.stats],
        });
        return;
      }
      NotificationService.error(data?.data?.metadata?.message);
      throw response.message;
    },
    onError: (err: any) => {
      throw err;
    },
  });
};
